import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Personas } from '@shared/models/personas.model';
import { UserService } from '@shared/services/user.service';
import posthog from 'posthog-js';
import { User } from '../../shared/models/user.model';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class MonitoringService {
  constructor(
    private storageService: StorageService,
    private userService: UserService
  ) {}

  /**
   * Initialize user context from stored user data
   * This is called on application startup to set Sentry and posthog context after page reload
   */
  initializeUserContext(): void {
    const user = this.storageService.getUser();
    if (user) {
      this.setSentryUser(user);
      this.setPosthogUser(user);
    }
  }

  /**
   * Set Sentry and posthog context with user details
   * @param user - The user object containing user details
   */
  setSentryUser(user: User): void {
    const selectedHospitals = this.userService.getSelectedHospitals();
    if (!user) {
      return;
    }

    // Set the user for Sentry
    Sentry.setUser({
      id: user._id,
      email: user.email,
      username: user.profile?.firstName + ' ' + user.profile?.lastName,
    });

    // Set additional user context
    Sentry.setContext('user_details', {
      hospitals: user?.profile?.hospitals?.map((hospital) => ({
        id: hospital._id,
        name: hospital.name,
        hospitalName: hospital.hospitalName,
      })),
      selectedHospitals: selectedHospitals,
      persona: (user.persona as Personas)?.name,
      personaId: (user.persona as Personas)?._id,
    });
  }

  setPosthogUser(user: User): void {
    posthog.reset();
    posthog.identify(user._id, {
      email: user.email,
      firstName: user.profile?.firstName,
      lastName: user.profile?.lastName,
      role: (user.persona as Personas).name,
    });

    posthog.group('organization', String(user.profile?.hospitals?.[0]?.hospitalName), {
      name: user.profile?.hospitals?.[0]?.hospitalName,
      service: user.profile?.hospitals?.[0]?.name,
    });
  }

  /**
   * Clear user context (called on logout)
   */
  clearUserContext(): void {
    Sentry.setUser(null);
    Sentry.setContext('user_details', null);
    posthog.reset();
  }
}
